import { useState } from "react"

import type { VisualizerCallTopicParent } from "./uploadedConversationsVisualizerDataset"

interface UploadedConversationsCallTopicTreeProps {
  onBucketClick: (bucketKey: string) => void
  onBucketHoverChange: (bucketKey: string | null) => void
  onParentClick: (conversationIds: string[]) => void
  parents: VisualizerCallTopicParent[]
}

export function UploadedConversationsCallTopicTree({
  onBucketClick,
  onBucketHoverChange,
  onParentClick,
  parents,
}: UploadedConversationsCallTopicTreeProps) {
  const [expandedParentKeys, setExpandedParentKeys] = useState<Set<string>>(
    () => new Set(),
  )

  const toggleParent = (parentKey: string) => {
    setExpandedParentKeys((current) => {
      const next = new Set(current)
      if (next.has(parentKey)) {
        next.delete(parentKey)
      } else {
        next.add(parentKey)
      }
      return next
    })
  }

  return (
    <div
      className="h-full min-h-0 overflow-y-auto p-1"
      data-testid="uploaded-conversations-call-topic-tree"
    >
      <ul className="space-y-2">
        {parents.map((parent) => {
          const expanded = expandedParentKeys.has(parent.parentId)
          const childrenId = `call-topic-children-${parent.parentId}`
          return (
            <li key={parent.parentId}>
              <div className="rounded-md border border-coval-border px-2 py-1.5">
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    aria-controls={childrenId}
                    aria-expanded={expanded}
                    className="shrink-0 text-xs text-coval-text-muted focus-visible:outline-none"
                    onClick={() => toggleParent(parent.parentId)}
                    data-testid={`uploaded-conversations-parent-expand-${parent.parentId}`}
                  >
                    {expanded ? "Collapse" : "Expand"}
                  </button>
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left focus-visible:outline-none"
                    onClick={() => onParentClick(parent.conversationIds)}
                    data-testid={`uploaded-conversations-parent-filter-${parent.parentId}`}
                  >
                    <span className="block text-sm font-medium text-coval-text">
                      {parent.title} ({parent.count})
                    </span>
                    <span className="block text-xs text-coval-text-muted">
                      {parent.description}
                    </span>
                  </button>
                </div>
                {expanded ? (
                  <ul id={childrenId} className="mt-2 space-y-1 pl-3">
                    {parent.children.map((child) => (
                      <li key={child.key}>
                        <button
                          type="button"
                          className="w-full rounded-sm px-1 py-1 text-left focus-visible:outline-none"
                          onClick={() => onBucketClick(child.key)}
                          onFocus={() => onBucketHoverChange(child.key)}
                          onBlur={() => onBucketHoverChange(null)}
                          onMouseEnter={() => onBucketHoverChange(child.key)}
                          onMouseLeave={() => onBucketHoverChange(null)}
                          data-testid={`uploaded-conversations-child-filter-${child.key}`}
                        >
                          <span className="block text-sm text-coval-text">
                            {child.label} ({child.count})
                          </span>
                          <span className="block text-xs text-coval-text-muted">
                            {child.description}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
